import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PageHeader } from '../components/PageHeader';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { Coffee, Clock, Utensils, Cake, ChevronRight, Egg, Home, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAFE_IMAGE = "https://images.unsplash.com/photo-1615200961449-0d6d5a818f8e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxjb2ZmZWUlMjBjYWtlJTIwY2FmZSUyMHRhYmxlfGVufDB8fHx8MTc3MDg1MTY3Mnww&ixlib=rb-4.1.0&q=85&w=800";

export function CafePage() {
    const navigate = useNavigate();
    const { settings } = useSettings();
    const { isAuthenticated, profile } = useAuth();
    const [menu, setMenu] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('hot_drinks');

    useEffect(() => {
        fetchMenu();
    }, []);

    const fetchMenu = async () => {
        try {
            const response = await axios.get(`${API}/cafe-menu`);
            setMenu(response.data);
        } catch (error) {
            console.error('Error fetching menu:', error);
        } finally {
            setLoading(false);
        }
    };

    const groupedMenu = menu.reduce((acc, item) => {
        if (!acc[item.category]) acc[item.category] = [];
        acc[item.category].push(item);
        return acc;
    }, {});

    const categoryLabels = {
        hot_drinks: { label: 'Hot Drinks', icon: Coffee },
        breakfast: { label: 'Breakfast', icon: Egg },
        lunch: { label: 'Lunch', icon: Utensils },
        desserts: { label: 'Desserts', icon: Cake },
    };

    // Define category order
    const categoryOrder = ['hot_drinks', 'breakfast', 'lunch', 'desserts'];
    const sortedCategories = categoryOrder.filter(cat => groupedMenu[cat]);

    // Check if user is a resident member
    const isResidentMember = profile?.is_resident && profile?.resident_membership_status === 'active';

    return (
        <div className="page-container" data-testid="cafe-page">
            <PageHeader title="The Restaurant" />
            
            {/* Hero Image */}
            <div className="relative h-48 overflow-hidden">
                <img
                    src={CAFE_IMAGE}
                    alt="Burston Restaurant"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute inset-0 flex items-end p-4">
                    <div className="bg-white/95 backdrop-blur rounded-2xl p-4 w-full">
                        <div className="flex items-center gap-2 text-primary">
                            <Clock className="w-5 h-5" />
                            <span className="font-bold">{settings.cafe_opening_times}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-4 py-4">
                {/* Special Banner */}
                <div className="card-base bg-accent/10 border-2 border-accent/30 mb-6" data-testid="cafe-special">
                    <div className="flex items-center gap-3">
                        <Cake className="w-8 h-8 text-accent" />
                        <div>
                            <p className="text-sm text-accent font-bold">Today's Special</p>
                            <p className="text-foreground font-medium">{settings.cafe_special_text}</p>
                        </div>
                    </div>
                </div>

                {/* Residents Club CTA */}
                <div 
                    className="card-interactive mb-6 residents-card border-2"
                    onClick={() => navigate(isResidentMember ? '/member/residents' : '/residents-club')}
                    data-testid="residents-club-cta"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(70 47% 42%)' }}>
                                {isResidentMember ? <Star className="w-6 h-6 text-white" /> : <Home className="w-6 h-6 text-white" />}
                            </div>
                            <div>
                                <h3 className="font-bold text-foreground">
                                    {isResidentMember ? 'Residents Discount' : 'Join Residents Club'}
                                </h3>
                                <p className="text-muted-foreground text-sm">
                                    {isResidentMember 
                                        ? 'Up to 20% off your meal as a member' 
                                        : 'Get up to 20% off in our restaurant'}
                                </p>
                            </div>
                        </div>
                        <ChevronRight className="w-6 h-6 text-muted-foreground" />
                    </div>
                </div>

                {/* Category Tabs */}
                <h2 className="text-xl font-bold text-foreground mb-4">Our Menu</h2>
                
                <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide" data-testid="menu-tabs">
                    {sortedCategories.map((category) => {
                        const CategoryIcon = categoryLabels[category]?.icon || Coffee;
                        return (
                            <button
                                key={category}
                                onClick={() => setActiveCategory(category)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                                    activeCategory === category 
                                        ? 'bg-primary text-white' 
                                        : 'bg-primary/10 text-primary'
                                }`}
                                data-testid={`tab-${category}`}
                            >
                                <CategoryIcon className="w-4 h-4" />
                                <span className="font-medium">{categoryLabels[category]?.label || category}</span>
                            </button>
                        );
                    })}
                </div>
                
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="cafe-menu">
                        {groupedMenu[activeCategory]?.map((item) => (
                            <div key={item.id} className="card-base">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1 pr-4">
                                        <p className="font-bold text-foreground">{item.name}</p>
                                        <p className="text-muted-foreground text-sm mt-1">{item.description}</p>
                                    </div>
                                    <span className="font-bold text-primary whitespace-nowrap text-lg">{item.price}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Allergy Note */}
                <div className="mt-6 card-base bg-primary/5">
                    <p className="text-muted-foreground text-sm text-center">
                        <strong>Allergies?</strong> Please inform a member of the restaurant team about any allergies before ordering.
                    </p>
                </div>

                {/* Note */}
                <div className="mt-4 text-center text-muted-foreground text-sm">
                    <p>Full menu available in the restaurant</p>
                    <p className="mt-1">Breakfast served until 11:30am • Lunch 11:30am-3pm</p>
                </div>
            </div>
        </div>
    );
}

export default CafePage;
